<template>
  <div class="project-single space-y-4">
    <section class="app-section space-y-4">
      <div class="project-topbar">
        <div class="project-heading">
          <h2 class="project-title">{{ editMode ? 'Редактирование проекта' : (project.title || 'Проект') }}</h2>
          <p class="project-subtitle">
            <template v-if="project.id">
              {{ project.category || 'Без категории' }}
              <span v-if="project.date"> · {{ formatProjectDate(project.date) }}</span>
            </template>
            <template v-else>
              Загрузка проекта...
            </template>
          </p>
        </div>

        <div class="project-actions">
          <ButtonBase color="gray" :onClick="showProjectList">← К проектам</ButtonBase>

          <template v-if="!editMode">
            <ButtonBase v-if="canEditProject" color="blue" :onClick="editProject">Изменить</ButtonBase>
            <ButtonBase v-if="canDeleteProject" color="red" :onClick="deleteProject">Удалить</ButtonBase>
            <ButtonBase v-if="canEditProject" :color="project.visible ? 'white' : 'gray'" @click="toggleVisibility">
              {{ project.visible ? 'Виден на сайте' : 'Скрыт на сайте' }}
            </ButtonBase>
            <ButtonBase v-if="canManageAll" :color="project.visible_shared ? 'white' : 'gray'" @click="toggleVisibilityShared">
              {{ project.visible_shared ? 'Виден в общем списке' : 'Скрыт в общем списке' }}
            </ButtonBase>
          </template>

          <template v-else>
            <ButtonBase color="gray" :onClick="cancelEdit">Отмена</ButtonBase>
            <ButtonBase color="green" :onClick="saveChanges">Сохранить изменения</ButtonBase>
          </template>
        </div>
      </div>

      <div v-if="!editMode" class="visibility-pills">
        <span class="status-pill" :class="project.visible ? 'on' : 'off'">
          Сайт: {{ project.visible ? 'Виден' : 'Скрыт' }}
        </span>
        <span class="status-pill" :class="project.visible_shared ? 'on' : 'off'">
          Общий список: {{ project.visible_shared ? 'Виден' : 'Скрыт' }}
        </span>
      </div>
    </section>

    <section v-if="editMode" class="app-section space-y-4">
      <h3 class="section-title">Основная информация</h3>

      <div class="project-form-grid">
        <FormInput
          v-model="editedProject.title"
          label="Название проекта"
          placeholder="Название проекта"
          required
          containerClasses="!mb-0"
        />

        <FormInput
          v-model="editedProject.category"
          type="select"
          label="Категория"
          :options="[
            { value: 'COMMERCIAL', label: 'COMMERCIAL' },
            { value: 'BROADCAST', label: 'BROADCAST' },
            { value: '3D', label: '3D' },
            { value: 'SOUND', label: 'SOUND' },
            { value: 'DESIGN', label: 'DESIGN' },
            { value: 'ABOUT', label: 'ABOUT' }
          ]"
          placeholder="Выберите категорию"
          required
          containerClasses="!mb-0"
        />

        <FormInput
          v-model="editedProject.date"
          type="date"
          label="Дата"
          required
          containerClasses="!mb-0"
        />

        <FormInput
          v-if="canEditPin"
          v-model="editedProject.pin"
          type="number"
          label="Порядок (pin)"
          placeholder="Чем меньше, тем выше"
          containerClasses="!mb-0"
        />
      </div>

      <div>
        <label class="mb-1 block text-sm font-medium text-foreground">Описание</label>
        <textarea
          v-model="editedProject.content"
          placeholder="Описание"
          class="input-field project-description-input"
          ref="descriptionTextarea"
          @input="autoResizeTextarea"
        ></textarea>
      </div>
    </section>

    <section v-else class="app-section space-y-3">
      <h3 class="section-title">Описание проекта</h3>

      <div class="project-meta-grid">
        <div class="meta-item">
          <span>Категория</span>
          <strong>{{ project.category || '—' }}</strong>
        </div>
        <div class="meta-item">
          <span>Дата</span>
          <strong>{{ project.date ? formatProjectDate(project.date) : '—' }}</strong>
        </div>
        <div class="meta-item">
          <span>Порядок (pin)</span>
          <strong>{{ project.pin ?? '—' }}</strong>
        </div>
      </div>

      <div class="project-description-view" v-html="project.content ? project.content.replace(/\n/g, '<br>') : 'Описание отсутствует'"></div>
    </section>

    <section class="app-section space-y-4">
      <h3 class="section-title">Медиа</h3>

      <template v-if="editMode">
        <div class="upload-grid">
          <FileUploadField
            label="Обложка"
            :current-file="coverCurrentFile"
            accept="image/*"
            accept-text="Изображения (JPG, PNG, WEBP)"
            :max-size="20 * 1024 * 1024"
            @file-uploaded="onCoverUploaded"
            @file-removed="onCoverRemoved"
            @file-cancelled="onCoverCancelled"
          />
          <FileUploadField
            label="Короткий ролик"
            :current-file="shortVideoCurrentFile"
            accept="video/*"
            accept-text="Видео (MP4, MOV, AVI)"
            :max-size="500 * 1024 * 1024"
            @file-uploaded="onShortVideoUploaded"
            @file-removed="onShortVideoRemoved"
            @file-cancelled="onShortVideoCancelled"
          />
          <FileUploadField
            label="Основное видео"
            :current-file="mainVideoCurrentFile"
            accept="video/*"
            accept-text="Видео (MP4, MOV, AVI)"
            :max-size="500 * 1024 * 1024"
            @file-uploaded="onMainVideoUploaded"
            @file-removed="onMainVideoRemoved"
            @file-cancelled="onMainVideoCancelled"
          />
          <FileUploadField
            label="Бэкстейдж видео"
            :current-file="backstageVideoCurrentFile"
            accept="video/*"
            accept-text="Видео (MP4, MOV, AVI)"
            :max-size="500 * 1024 * 1024"
            @file-uploaded="onBackstageVideoUploaded"
            @file-removed="onBackstageVideoRemoved"
            @file-cancelled="onBackstageVideoCancelled"
          />
        </div>
      </template>

      <template v-else>
        <div class="media-grid" v-if="hasAnyMedia">
          <div v-if="project.cover_url" class="media-card">
            <h4>Обложка</h4>
            <img class="media" :src="project.cover_url" alt="Cover image" />
          </div>

          <div v-if="project.short_video_url" class="media-card">
            <h4>Короткий ролик</h4>
            <video class="media" controls>
              <source :src="project.short_video_url" type="video/mp4" />
              Ваш браузер не поддерживает видео.
            </video>
          </div>

          <div v-if="project.main_video_url" class="media-card">
            <h4>Основное видео</h4>
            <video class="media" controls>
              <source :src="project.main_video_url" type="video/mp4" />
              Ваш браузер не поддерживает видео.
            </video>
          </div>

          <div v-if="project.backstage_video_url" class="media-card">
            <h4>Backstage видео</h4>
            <video class="media" controls>
              <source :src="project.backstage_video_url" type="video/mp4" />
              Ваш браузер не поддерживает видео.
            </video>
          </div>
        </div>
        <div v-else class="empty-state">Медиафайлы пока не загружены</div>
      </template>

      <div class="backstage-box">
        <BackstageGalleryInline
          title="Backstage галерея проекта"
          context="project"
          :context-id="project.id"
          :allow-reorder="editMode"
          :show-info="editMode"
          :show-actions="editMode"
        />
      </div>
    </section>

    <section class="app-section space-y-4">
      <div class="participants-head">
        <h3 class="section-title m-0">Участники проекта</h3>
        <ButtonBase v-if="editMode && canEditParticipants" color="blue" :onClick="addParticipant">Добавить участника</ButtonBase>
      </div>

      <ul v-if="!editMode" class="participants-list">
        <li v-for="userInfo in projectUsers" :key="`${userInfo.user.id}-${userInfo.role}`" class="participant-card">
          <div class="participant-main">
            <h4>{{ userInfo.user.title }}</h4>
            <span class="participant-role">{{ userInfo.role }}</span>
          </div>
          <p class="participant-description">{{ userInfo.user.description || 'Без позиции' }}</p>
          <div class="markdown-content" v-html="renderMarkdown(userInfo.user.long_description)" />
        </li>
      </ul>

      <div v-else class="participants-edit-list">
        <div v-for="(participant, index) in editedParticipants" :key="index" class="participant-row">
          <select v-model="participant.user_id" class="input-field participant-select" :disabled="!canEditParticipants">
            <option value="" disabled>Выберите участника</option>
            <option v-for="user in localUsers" :key="user.id" :value="user.id">{{ user.title }}</option>
          </select>

          <FormAutocomplete
            v-if="canEditParticipants"
            v-model="participant.roleSearch"
            :options="roles"
            placeholder="Начните вводить роль"
            width="100%"
            @update:modelValue="val => selectRole(index, val)"
          />
          <input
            v-else
            class="input-field participant-role-readonly"
            type="text"
            :value="participant.role || 'Роль не указана'"
            readonly
          />

          <ButtonBase v-if="canEditParticipants" color="red" :onClick="() => removeParticipant(index)">Удалить</ButtonBase>
        </div>

        <p v-if="!canEditParticipants" class="empty-note">
          Изменение участников доступно только администраторам и суперюзерам.
        </p>
        <p v-if="editedParticipants.length === 0" class="empty-note">Добавьте участников для проекта</p>
      </div>
    </section>

    <div v-if="showDeleteModal" class="modal" @click.self="showDeleteModal = false">
      <div class="modal-content">
        <h3>Вы уверены, что хотите удалить проект?</h3>
        <p class="modal-note">Это действие нельзя отменить.</p>
        <div class="modal-actions">
          <ButtonBase color="red" :onClick="confirmDeleteProject">Удалить</ButtonBase>
          <ButtonBase color="gray" :onClick="() => { showDeleteModal = false }">Отмена</ButtonBase>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import { fetchProject } from '../services/api';
import { getUsersApi } from '../services/api';
import { fetchPublicUsersApi } from '../services/api';
import { updateProjectApi } from '../services/api';
import { deleteProjectApi } from '../services/api';
import { useToast } from 'vue-toastification';
import ButtonBase from './ButtonBase.vue';
import FormInput from './FormInput.vue';
import FormAutocomplete from './FormAutocomplete.vue';
import FileUploadField from './FileUploadField.vue';
import BackstageGalleryInline from './BackstageGalleryInline.vue';

export default {
  props: {
    projectID: Number,
    showProjectList: Function,
    currentUser: {
      type: Object,
      default: () => ({})
    },
    canManageAll: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ButtonBase,
    FormInput,
    FormAutocomplete,
    FileUploadField,
    BackstageGalleryInline
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    hasAnyMedia() {
      return Boolean(
        this.project.cover_url ||
        this.project.short_video_url ||
        this.project.main_video_url ||
        this.project.backstage_video_url
      );
    },
    projectUsers() {
      return Array.isArray(this.project.users) ? this.project.users : [];
    },
    isCurrentUserParticipant() {
      const currentUserId = this.currentUser?.id;
      if (!currentUserId) return false;
      return this.projectUsers.some(item => item?.user?.id === currentUserId);
    },
    canEditProject() {
      return this.canManageAll || this.isCurrentUserParticipant;
    },
    canDeleteProject() {
      return this.canManageAll;
    },
    canEditPin() {
      return this.canManageAll;
    },
    canEditParticipants() {
      return this.canEditProject;
    },
    editedParticipants() {
      return Array.isArray(this.editedProject.participants) ? this.editedProject.participants : [];
    },
    coverCurrentFile() {
      const originalId = this.project?.cover_id;
      const hasOriginal = Boolean(this.project?.cover_url || originalId);
      if (!hasOriginal || this.editedProject?.cover_id === null) return null;
      if (originalId && this.editedProject?.cover_id && this.editedProject.cover_id !== originalId) return null;
      return {
        id: originalId || 'project-cover',
        name: 'Текущая обложка',
        original_filename: 'cover',
        url: this.project?.cover_url || null
      };
    },
    shortVideoCurrentFile() {
      const originalId = this.project?.short_video_id;
      const hasOriginal = Boolean(this.project?.short_video_url || originalId);
      if (!hasOriginal || this.editedProject?.short_video_id === null) return null;
      if (originalId && this.editedProject?.short_video_id && this.editedProject.short_video_id !== originalId) return null;
      return {
        id: originalId || 'project-short-video',
        name: 'Текущий короткий ролик',
        original_filename: 'short_video',
        url: this.project?.short_video_url || null
      };
    },
    mainVideoCurrentFile() {
      const originalId = this.project?.main_video_id;
      const hasOriginal = Boolean(this.project?.main_video_url || originalId);
      if (!hasOriginal || this.editedProject?.main_video_id === null) return null;
      if (originalId && this.editedProject?.main_video_id && this.editedProject.main_video_id !== originalId) return null;
      return {
        id: originalId || 'project-main-video',
        name: 'Текущее основное видео',
        original_filename: 'main_video',
        url: this.project?.main_video_url || null
      };
    },
    backstageVideoCurrentFile() {
      const originalId = this.project?.backstage_video_id;
      const hasOriginal = Boolean(this.project?.backstage_video_url || originalId);
      if (!hasOriginal || this.editedProject?.backstage_video_id === null) return null;
      if (originalId && this.editedProject?.backstage_video_id && this.editedProject.backstage_video_id !== originalId) return null;
      return {
        id: originalId || 'project-backstage-video',
        name: 'Текущее backstage видео',
        original_filename: 'backstage_video',
        url: this.project?.backstage_video_url || null
      };
    }
  },
  methods: {
    formatProjectDate(date) {
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) {
        return '—';
      }
      return parsed.toLocaleDateString('ru-RU');
    },
    cancelEdit() {
      this.editMode = false;
    },
    editProject: async function () {
      if (!this.canEditProject) {
        return;
      }
      this.editMode = true;
      this.editedProject = JSON.parse(JSON.stringify(this.project));

      if (this.editedProject.date) {
        const dateObj = new Date(this.editedProject.date);
        this.editedProject.date = dateObj.toISOString().split('T')[0];
      }

      this.editedProject.participants = (this.project.users || []).map(userRole => ({
        role: userRole.role || '',
        user_id: userRole.user?.id || '',
        roleSearch: userRole.role || '',
        showDropdown: false,
        filteredRoles: [...this.roles]
      }));

      if (!this.localUsers.length) {
        try {
          const fetchedUsers = this.canManageAll
            ? await getUsersApi()
            : await fetchPublicUsersApi();
          const projectUsers = this.projectUsers
            .map(item => item?.user)
            .filter(Boolean)
            .map(user => ({
              id: user.id,
              title: user.title || user.username || 'Пользователь'
            }));

          const usersMap = new Map();
          [...fetchedUsers, ...projectUsers].forEach(user => {
            if (!user?.id) {
              return;
            }
            usersMap.set(String(user.id), user);
          });
          this.localUsers = Array.from(usersMap.values());
        } catch (e) {
          console.error('Ошибка загрузки пользователей:', e);
          this.localUsers = this.projectUsers
            .map(item => item?.user)
            .filter(Boolean)
            .map(user => ({
              id: user.id,
              title: user.title || user.username || 'Пользователь'
            }));
        }
      }
    },
    saveChanges: async function () {
      function getProjectDiff(original, edited) {
        const diff = {};
        for (const key in edited) {
          if (key === 'participants') {
            const orig = (original.users || []).map(u => ({ role: u.role, user_id: u.user?.id }));
            const curr = (edited.participants || []).map(u => ({ role: u.role, user_id: u.user_id }));
            if (JSON.stringify(orig) !== JSON.stringify(curr)) {
              diff.participants = curr;
            }
          } else if (key === 'users' || key === 'roleSearch' || key === 'showDropdown' || key === 'filteredRoles') {
            continue;
          } else if (edited[key] !== original[key]) {
            diff[key] = edited[key];
          }
        }
        return diff;
      }

      const diff = getProjectDiff(this.project, this.editedProject);
      if (!this.canEditPin) {
        delete diff.pin;
      }
      if (!this.canEditParticipants) {
        delete diff.participants;
      }
      if (Object.keys(diff).length === 0) {
        this.editMode = false;
        return;
      }

      try {
        await updateProjectApi(this.project.id, diff);
        this.showProjectList();
      } catch (e) {
        this.toast.error(e?.message || 'Ошибка при сохранении изменений проекта');
      }
    },
    addParticipant() {
      if (!this.canEditParticipants) {
        return;
      }
      this.editedProject.participants.push({
        role: '',
        user_id: '',
        roleSearch: '',
        showDropdown: false,
        filteredRoles: [...this.roles]
      });
    },
    removeParticipant(index) {
      if (!this.canEditParticipants) {
        return;
      }
      this.editedProject.participants.splice(index, 1);
    },
    openDropdown(index) {
      this.editedProject.participants[index].showDropdown = true;
    },
    filterRoles(index) {
      const searchTerm = this.editedProject.participants[index].roleSearch.toLowerCase();
      this.editedProject.participants[index].filteredRoles = this.roles.filter(role =>
        role.toLowerCase().includes(searchTerm)
      );
      this.editedProject.participants[index].showDropdown = true;
    },
    filteredRoles(index) {
      return this.editedProject.participants[index].filteredRoles;
    },
    selectRole(index, role) {
      this.editedProject.participants[index].role = role;
      this.editedProject.participants[index].roleSearch = role;
      this.editedProject.participants[index].showDropdown = false;
    },
    deleteProject() {
      if (!this.canDeleteProject) {
        return;
      }
      this.showDeleteModal = true;
    },
    async confirmDeleteProject() {
      if (this.project && this.project.id) {
        await deleteProjectApi(this.project.id);
        this.showProjectList();
      }
      this.showDeleteModal = false;
    },
    async fetchProjectLocal() {
      const projectId = this.projectID;
      this.project = await fetchProject(projectId);
    },
    autoResizeTextarea() {
      this.$nextTick(() => {
        const textarea = this.$refs.descriptionTextarea;
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = textarea.scrollHeight + 'px';
        }
      });
    },
    toggleVisibility: async function () {
      if (!this.canEditProject) {
        return;
      }
      try {
        await updateProjectApi(this.project.id, { visible: !this.project.visible });
        await this.fetchProjectLocal();
      } catch (error) {
        this.toast.error(error?.message || 'Ошибка при изменении видимости проекта');
      }
    },
    toggleVisibilityShared: async function () {
      if (!this.canManageAll) {
        return;
      }
      try {
        await updateProjectApi(this.project.id, { visible_shared: !this.project.visible_shared });
        await this.fetchProjectLocal();
      } catch (error) {
        this.toast.error(error?.message || 'Ошибка при изменении видимости проекта в общем списке');
      }
    },
    onCoverUploaded(file) {
      this.editedProject.cover_id = file?.id || null;
    },
    onCoverRemoved() {
      this.editedProject.cover_id = null;
    },
    onCoverCancelled() {
      this.editedProject.cover_id = this.project?.cover_id || null;
    },
    onShortVideoUploaded(file) {
      this.editedProject.short_video_id = file?.id || null;
    },
    onShortVideoRemoved() {
      this.editedProject.short_video_id = null;
    },
    onShortVideoCancelled() {
      this.editedProject.short_video_id = this.project?.short_video_id || null;
    },
    onMainVideoUploaded(file) {
      this.editedProject.main_video_id = file?.id || null;
    },
    onMainVideoRemoved() {
      this.editedProject.main_video_id = null;
    },
    onMainVideoCancelled() {
      this.editedProject.main_video_id = this.project?.main_video_id || null;
    },
    onBackstageVideoUploaded(file) {
      this.editedProject.backstage_video_id = file?.id || null;
    },
    onBackstageVideoRemoved() {
      this.editedProject.backstage_video_id = null;
    },
    onBackstageVideoCancelled() {
      this.editedProject.backstage_video_id = this.project?.backstage_video_id || null;
    },
    renderMarkdown(value) {
      const text = (value || '').trim();
      if (!text) return '';
      const escapedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return marked.parse(escapedText, {
        gfm: true,
        breaks: true
      });
    },
  },
  data() {
    return {
      project: {},
      editMode: false,
      editedProject: {},
      roles: [
        'Режиссёр', 'Продюсер', 'Сценарист', 'Оператор-постановщик', 'Художник-постановщик', 'Второй режиссёр', 'Звукорежиссёр', 'Художник по костюмам', 'Художник по гриму', 'Художник по реквизиту', 'Гафер', 'Оператор', 'Скрипт-супервайзер', 'Помощник режиссёра', 'Осветитель', 'Актёр', 'Каскадёр', 'Режиссёр монтажа', 'Монтажёр', 'Колорист', 'Супервайзер визуальных эффектов (VFX)', 'Композитинг-артист', '3D-художник', 'Аниматор', 'Режиссёр по работе с актёрами', 'Кастинг-директор', 'Директор фильма', 'Администратор площадки', 'Локационный менеджер', 'Постановщик трюков', 'Пиротехник', 'Специалист по спецэффектам (SFX)', 'Дольщик (Steadicam-оператор)', 'Оператор крана', 'Оператор дрона', 'Подводный оператор', 'Бум-оператор (звукоуловитель)', 'Микрофонный оператор', 'Диктор', 'Композитор', 'Саунд-дизайнер', 'Фолей-артист (озвучка шумов)', 'Редактор титров', 'Субтитрёр', 'Режиссёр дубляжа', 'Переводчик', 'Продюсер постпродакшна', 'Технический директор', 'Ассистент оператора', 'Фотограф со съёмочной площадки', 'Стилист', 'Декоратор', 'Бутафор', 'Режиссёр клипов', 'Режиссёр документального кино', 'Режиссёр анимационного кино', 'Сторибордист (художник раскадровки)', 'Режиссёр прямого эфира', 'Телевизионный продюсер', 'Ведущий (TV/стримов)', 'Спортивный комментатор', 'Аналитик в эфире', 'Видеоинженер', 'Видеооператор в студии', 'Оператор видеомикшера', 'Графический оператор (визуализация в эфире)', 'Супервайзер телетрансляции', 'Техник телеметрии', 'Специалист по хромакею', 'Мультикам-оператор (многокамерная съёмка)', 'Режиссёр повторов (в спорте)', 'Оператор табло/счётчика', 'Супервайзер медленных повторов (Slow Motion Operator)', 'Звукорежиссёр прямого эфира', 'Микрофонный ассистент (в студии/на поле)', 'Светорежиссёр (в студии)', 'Сет-дизайнер (оформление студии)', 'Сценарист телепередач', 'Редактор эфира', 'Ассистент продюсера', 'Координатор гостей в студии', 'Суфлёр', 'Техник по телекоммуникациям', 'Специалист по потоковому вещанию (стримингу)'
      ],
      localUsers: [],
      showDeleteModal: false
    };
  },
  mounted() {
    this.fetchProjectLocal();
  },
};
</script>

<style scoped>
.project-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.project-heading {
  min-width: 240px;
}

.project-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.project-subtitle {
  margin: 6px 0 0;
  font-size: 0.9rem;
  color: hsl(var(--muted-foreground));
}

.project-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.visibility-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid;
}

.status-pill.on {
  background: hsl(var(--success) / 0.12);
  border-color: hsl(var(--success) / 0.35);
  color: hsl(var(--success));
}

.status-pill.off {
  background: hsl(var(--destructive) / 0.12);
  border-color: hsl(var(--destructive) / 0.35);
  color: hsl(var(--destructive));
}

.section-title {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.project-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.project-description-input {
  min-height: 120px;
  resize: vertical;
  line-height: 1.45;
}

.project-meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.meta-item {
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--muted));
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-item span {
  font-size: 0.75rem;
  color: hsl(var(--muted-foreground));
}

.meta-item strong {
  font-size: 0.9rem;
  color: hsl(var(--foreground));
}

.project-description-view {
  padding: 12px;
  border-radius: 10px;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--card));
  color: hsl(var(--foreground));
  line-height: 1.55;
}

.upload-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.media-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.media-card {
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  background: hsl(var(--card));
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.media-card h4 {
  margin: 0;
  font-size: 0.88rem;
  color: hsl(var(--foreground));
}

.media {
  width: 100%;
  max-height: 340px;
  object-fit: cover;
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: #000;
}

.backstage-box {
  border-top: 1px solid hsl(var(--border));
  padding-top: 12px;
}

.empty-state,
.empty-note {
  border: 1px dashed hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--muted));
  color: hsl(var(--muted-foreground));
  padding: 12px;
  text-align: center;
  margin: 0;
}

.participants-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.participants-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
}

.participant-card {
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  background: hsl(var(--card));
  padding: 12px;
}

.participant-main {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.participant-main h4 {
  margin: 0;
  font-size: 0.96rem;
  color: hsl(var(--foreground));
}

.participant-role {
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.72rem;
  border: 1px solid hsl(var(--accent) / 0.35);
  background: hsl(var(--accent) / 0.12);
  color: hsl(var(--accent));
}

.participant-description {
  margin: 8px 0;
  color: hsl(var(--muted-foreground));
  font-size: 0.88rem;
}

.participants-edit-list {
  display: grid;
  gap: 10px;
}

.participant-row {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.4fr) auto;
  gap: 8px;
  align-items: start;
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  background: hsl(var(--card));
  padding: 10px;
}

.participant-select {
  min-height: 36px;
}

.participant-role-readonly {
  cursor: default;
}

.modal {
  position: fixed;
  inset: 0;
  background-color: hsl(var(--foreground) / 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 60;
  padding: 16px;
}

.modal-content {
  width: min(460px, 100%);
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  padding: 18px;
  background: hsl(var(--card));
  box-shadow: 0 20px 40px hsl(var(--foreground) / 0.2);
}

.modal-content h3 {
  margin: 0;
}

.modal-note {
  margin: 8px 0 0;
  font-size: 0.9rem;
  color: hsl(var(--muted-foreground));
}

.modal-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.markdown-content :deep(p),
.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin: 0 0 8px;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  padding-left: 20px;
}

.markdown-content :deep(strong),
.markdown-content :deep(b) {
  font-weight: 700;
}

.markdown-content :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

:global(.dark) .markdown-content :deep(a) {
  color: #60a5fa;
}

@media (max-width: 960px) {
  .project-form-grid,
  .project-meta-grid {
    grid-template-columns: 1fr;
  }

  .participant-row {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-wrap: wrap;
  }
}
</style>
